import { ConflictError, NotFoundError } from '@domain/errors';
import type {
  City,
  CompanyPerformance,
  Country,
  CountryDraft,
  Formula,
  FormulaDraft,
  NotificationLogEntry,
  NotificationTemplates,
  OfferTransaction,
  PerformanceTotals,
  PregnancyWeek,
  ReminderSettings,
} from '@domain/entities';
import type {
  CityRepository,
  CountryRepository,
  FormulaRepository,
  NotificationRepository,
  PerformanceQuery,
  PerformanceRepository,
  PregnancyRepository,
} from '@domain/repositories';
import { buildPage } from '@domain/value-objects';
import type { NotificationChannel, NotificationType, Page } from '@domain/value-objects';
import { seedCities, seedTransactions } from '../../seed/dataset';
import { InMemoryDb, latency, matches } from './in-memory-db';

export class InMemoryFormulaRepository implements FormulaRepository {
  constructor(private readonly db: InMemoryDb) {}

  async list(): Promise<Formula[]> {
    return latency([...this.db.formulas], 120);
  }

  async create(draft: FormulaDraft): Promise<Formula> {
    const created: Formula = {
      ...draft,
      description: draft.description ?? null,
      id: this.db.nextId('form'),
      sortOrder: draft.sortOrder ?? this.db.formulas.length + 1,
      isActive: draft.isActive ?? true,
    };
    this.db.formulas = [...this.db.formulas, created];
    return latency(created, 140);
  }

  async update(id: string, draft: FormulaDraft): Promise<Formula> {
    const index = this.db.formulas.findIndex((f) => f.id === id);
    if (index === -1) throw new NotFoundError('Formule', id);
    const updated: Formula = { ...this.db.formulas[index], ...draft };
    this.db.formulas[index] = updated;
    return latency(updated, 120);
  }

  async remove(id: string): Promise<void> {
    const index = this.db.formulas.findIndex((f) => f.id === id);
    if (index === -1) throw new NotFoundError('Formule', id);
    const subscribed = false; // in-memory stub: Company has no formulaIndex field
    if (subscribed) {
      throw new ConflictError('Formule souscrite par au moins une entreprise active.');
    }
    this.db.formulas = this.db.formulas.filter((f) => f.id !== id);
    await latency(null, 120);
  }

  async getReminder(): Promise<ReminderSettings> {
    return latency({ ...this.db.reminder }, 80);
  }

  async saveReminder(settings: ReminderSettings): Promise<ReminderSettings> {
    this.db.reminder = { ...settings };
    return latency({ ...this.db.reminder }, 120);
  }
}

export class InMemoryCityRepository implements CityRepository {
  private readonly cities: City[] = seedCities();
  async list(): Promise<City[]> {
    return latency([...this.cities], 80);
  }
}

export class InMemoryCountryRepository implements CountryRepository {
  constructor(private readonly db: InMemoryDb) {}

  async list(): Promise<Country[]> {
    return latency([...this.db.countries], 120);
  }

  async create(draft: CountryDraft): Promise<Country> {
    if (this.db.countries.some((c) => c.code === draft.code)) {
      throw new ConflictError(`Le pays ${draft.code} existe déjà.`);
    }
    const created: Country = { ...draft, id: this.db.nextId('ct'), isActive: true, sortOrder: this.db.countries.length + 1 };
    this.db.countries = [...this.db.countries, created];
    return latency(created, 140);
  }

  async update(id: string, draft: CountryDraft): Promise<Country> {
    const index = this.db.countries.findIndex((c) => c.id === id);
    if (index === -1) throw new NotFoundError('Pays', id);
    const updated: Country = { ...this.db.countries[index], ...draft };
    this.db.countries[index] = updated;
    return latency(updated, 120);
  }

  async toggleActive(id: string): Promise<Country> {
    const index = this.db.countries.findIndex((c) => c.id === id);
    if (index === -1) throw new NotFoundError('Pays', id);
    const updated: Country = {
      ...this.db.countries[index],
      isActive: !this.db.countries[index].isActive,
    };
    this.db.countries[index] = updated;
    return latency(updated, 100);
  }

  async remove(id: string): Promise<void> {
    if (!this.db.countries.some((c) => c.id === id)) throw new NotFoundError('Pays', id);
    this.db.countries = this.db.countries.filter((c) => c.id !== id);
    await latency(null, 120);
  }
}

export class InMemoryPregnancyRepository implements PregnancyRepository {
  constructor(private readonly db: InMemoryDb) {}

  async list(search: string): Promise<PregnancyWeek[]> {
    const filtered = this.db.weeks.filter(
      (w) => matches(w.fruit, search) || matches(`semaine ${w.week} sa ${w.week}`, search),
    );
    return latency(filtered, 120);
  }

  async get(week: number): Promise<PregnancyWeek> {
    const found = this.db.weeks.find((w) => w.week === week);
    if (!found) throw new NotFoundError('Semaine', String(week));
    return latency(found, 80);
  }

  async save(week: number, draft: import('@domain/entities').PregnancyWeekDraft): Promise<PregnancyWeek> {
    const index = this.db.weeks.findIndex((w) => w.week === week);
    const updated: PregnancyWeek = {
      week,
      validated: false,
      imageUrl: null,
      ...draft,
    };
    if (index === -1) {
      this.db.weeks.push(updated);
    } else {
      this.db.weeks[index] = updated;
    }
    return latency(updated, 120);
  }

  async validate(week: number): Promise<PregnancyWeek> {
    const index = this.db.weeks.findIndex((w) => w.week === week);
    if (index === -1) throw new NotFoundError('Semaine', String(week));
    const updated: PregnancyWeek = { ...this.db.weeks[index], validated: true };
    this.db.weeks[index] = updated;
    return latency(updated, 120);
  }
}

export class InMemoryNotificationRepository implements NotificationRepository {
  constructor(private readonly db: InMemoryDb) {}

  async getTemplates(): Promise<NotificationTemplates> {
    return latency({ ...this.db.templates }, 80);
  }

  async saveTemplate(
    type: NotificationType,
    patch: Partial<{ subject: string; body: string; channel: NotificationChannel }>,
  ): Promise<NotificationTemplates> {
    this.db.templates = {
      ...this.db.templates,
      [type]: { ...this.db.templates[type], ...patch },
    };
    return latency({ ...this.db.templates }, 100);
  }

  async listLog(limit: number): Promise<NotificationLogEntry[]> {
    return latency(this.db.notificationLog.slice(0, limit), 80);
  }

  async append(
    type: NotificationType,
    company: string,
    subject: string,
  ): Promise<NotificationLogEntry> {
    const entry: NotificationLogEntry = {
      id: this.db.nextId('log'),
      type,
      company,
      subject,
      channel: this.db.templates[type].channel,
      sentAt: new Date().toLocaleDateString('fr-FR'),
    };
    this.db.notificationLog = [entry, ...this.db.notificationLog];
    return entry;
  }
}

export class InMemoryPerformanceRepository implements PerformanceRepository {
  constructor(private readonly db: InMemoryDb) {}

  private rows(): CompanyPerformance[] {
    const formatter = new Intl.NumberFormat('fr-FR');
    return this.db.companies
      .filter((c) => c.status === 'approved')
      .map((c, i): CompanyPerformance => {
        const formula = this.db.formulas[i % this.db.formulas.length] ?? this.db.formulas[0];
        const revenueSubscriptions = formula ? formula.amountXof : 0;
        const revenueOffers = this.db.offers.filter((o) => o.company === c.name && o.status === 'active').length * 5000;
        return {
          id: c.id,
          name: c.name,
          type: c.type,
          city: c.city,
          formulaLabel: formula ? `${formula.label} — ${formatter.format(formula.amountXof)} FCFA` : '—',
          offersCount: this.db.offers.filter((o) => o.company === c.name && o.status === 'active')
            .length,
          revenue: revenueSubscriptions + revenueOffers,
          revenueSubscriptions,
          revenueOffers,
        };
      });
  }

  async search(query: PerformanceQuery): Promise<Page<CompanyPerformance>> {
    const direction = query.sortDir === 'asc' ? 1 : -1;
    const filtered = this.rows()
      .filter((row) => matches(row.name, query.search))
      .sort((a, b) => {
        const left = a[query.sortBy];
        const right = b[query.sortBy];
        return typeof left === 'string' && typeof right === 'string'
          ? left.localeCompare(right, 'fr') * direction
          : (Number(left) - Number(right)) * direction;
      });
    return latency(buildPage(filtered, query));
  }

  async totals(): Promise<PerformanceTotals> {
    const rows = this.rows();
    return latency(
      {
        totalRevenue: rows.reduce((sum, row) => sum + row.revenue, 0),
        revenueSubscriptions: rows.reduce((sum, row) => sum + row.revenueSubscriptions, 0),
        revenueOffers: rows.reduce((sum, row) => sum + row.revenueOffers, 0),
        totalUsages: seedTransactions(this.db.users, this.db.offers).length,
        activeCompanies: rows.length,
        activeUsers: this.db.users.filter((u) => u.status === 'active').length,
      },
      120,
    );
  }

  async journal(search: string, limit: number): Promise<OfferTransaction[]> {
    const all = seedTransactions(this.db.users, this.db.offers);
    const filtered = all.filter((tx) =>
      matches(`${tx.userName} ${tx.companyName} ${tx.offerTitle}`, search),
    );
    return latency(filtered.slice(0, limit), 140);
  }
}
